import {
  groupBy,
  startsWith,
  endsWith,
  sortBy,
  prop,
  compose,
  mapObjIndexed,
} from 'ramda';
import ordersWizardTpl from './ordersWizard.tpl.html';

export class OrdersWizard {
  constructor($scope, $ngRedux, actionsFactory, companiesService,
    $anchorScroll, $location, $translate) {
    Object.assign(this, {
      $scope,
      $ngRedux,
      actionsFactory,
      companiesService,
      $anchorScroll,
      $location,
      $translate,
      search: {},
      activeStep: 0,
    });

    this.searchOptions = companiesService.getSearchOptions();
    this.orderParams = {};
    this.notes = {
      representativeName: '',
      representativeEmail: '',
      focusPoints: '',
    };
    this.activeSearchType = 'italy';
    this.activeSearch = 0;
    this.productSelected = false;
    this.price = 0;
    this.unsubscribeRedux = $ngRedux.connect(OrdersWizard.mapStateToThis, actionsFactory)(this);
    this.usubscribeScopeListener = $scope.$on('companies-search-ended', () => {
      $location.hash('tct-orders-wizard__search-results');
      $anchorScroll();
    });
    this.privacyAccepted = null;
    this.checkForeignValidation = true;
  }

  static mapStateToThis(state) {
    const getProduct = prop('product');
    const isQuickReport = compose(startsWith('QUICK_REPORT'), getProduct);
    const isBase = compose(startsWith('BASE'), getProduct);
    const isTop = compose(startsWith('TOP'), getProduct);
    const isNew = compose(endsWith('NEW'), getProduct);
    const isExisting = compose(endsWith('EXISTING'), getProduct);

    const byProductLevel = groupBy((entry) => {
      if (isQuickReport(entry)) {
        return 'QUICK_REPORT';
      }

      if (isBase(entry)) {
        return 'BASE';
      }

      if (isTop(entry)) {
        return 'TOP';
      }

      return 'PREMIUM';
    });

    const sortProducts = sortBy((a) => {
      if (isExisting(a)) return -1;
      if (isNew(a)) return 1;
      return 0;
    });

    return {
      auth: state.authentication,
      results: state.search.results,
      isFetching: state.search.loading || state.priceList.loading || state.orders.pending.loading,
      priceEntries: mapObjIndexed(
        sortProducts,
        byProductLevel(state.priceList.entries)),
      credits: {
        start: state.priceList.availableCredits,
        left: state.priceList.availableCredits,
      },
    };
  }

  $onDestroy() {
    this.unsubscribeRedux();
    this.usubscribeScopeListener();
  }

  $onInit() {
    if (this.results.length) {
      this.resetCompanyList();
    }
    this.$location.hash('');
  }

  resetSearch() {
    this.search = {};
    this.privacyAccepted = null;
  }

  acceptPrivacy() {
    this.privacyAccepted = true;
  }

  checkForeignFields() {
    this.checkForeignValidation = (
      this.searchForm.name.$modelValue !== '' ||
      this.searchForm.registrationNumber.$modelValue !== ''
    );
  }

  nextStep(result) {
    this.activeStep += 1;
    this.companySelected = result;
    this.$location.hash('');
    if (this.activeSearchType === 'italy') {
      this.italianOrderPriceEntriesRequest(this.companySelected);
    } else {
      const registrationNumber = (this.companySelected.registration_numbers.length > 0)
        ? this.companySelected.registration_numbers[0].value
        : '';
      this.companySelected.registration_number = registrationNumber;
      this.foreignOrderPriceEntriesRequest(this.companySelected, this.search.country);
    }
  }

  prevStep() {
    this.productSelected = false;
    this.activeStep -= 1;
  }

  setSearchType(type) {
    this.activeSearchType = type;
    this.activeSearch = 0;
    this.search = {};
    this.privacyAccepted = false;
  }

  doSearch() {
    const actionData = Object.assign({}, this.search, {
      searchApi: this.searchOptions[this.activeSearchType].radios[this.activeSearch].searchApi,
      privacyAccepted: this.privacyAccepted,
    });

    this.searchCompany(actionData);
  }

  searchParamSelected(item, model, searchParam) {
    this.search[searchParam] = item && item.id;
    if (this.activeSearchType !== 'italy') {
      this.search.countryName = item && item.name;
    }
  }

  selectProduct($event) {
    this.resetOrder();
    if (this.activeSearchType === 'italy') {
      this.orderParams = {
        name: this.companySelected.name,
        rea: this.companySelected.rea_number,
        cciaa: this.companySelected.cciaa.code,
        fiscal_code: this.companySelected.fiscal_code,
        vat_number: this.companySelected.vat_number,
        status: this.companySelected.status.code,
        product: '',
        reason: '',
      };
    } else {
      this.orderParams = {
        name: this.companySelected.name,
        duns: this.companySelected.duns,
        registration_number: this.companySelected.registration_number,
        country: this.search.country,
        product: '',
        reason: '',
      };
    }
    this.price = $event.target.getAttribute('data-price');
    this.orderParams.product = $event.target.getAttribute('data-product');
    if (!this.productSelected) {
      this.productSelected = true;
    }
    this.credits.left = this.credits.start - this.price;
  }

  resetOrder() {
    this.productSelected = false;
    this.orderParams = {};
    this.notes = {
      investigationCode: '',
      investigationSubCode: '',
      representativeFirstName: '',
      representativeLastName: '',
      representativeEmail: '',
      representativePhone: '',
      address: '',
      reason: '',
    };
  }

  placeOrder() {
    const creditsLeft = this.credits.left;

    if (this.isContactPersonRequired()) {
      if (this.activeSearchType === 'italy') {
        this.placeOrderNationalStart(Object.assign({}, this.orderParams, {
          reason: this.notes.reason,
          address: this.notes.address,
          investigation: {
            code: this.notes.investigationCode,
            subcode: this.notes.investigationSubCode,
          },
          representative: {
            first_name: this.notes.representativeFirstName,
            last_name: this.notes.representativeLastName,
            email: this.notes.representativeEmail,
            phone: this.notes.representativePhone,
          },
        }));
      } else {
        this.placeOrderForeignStart(Object.assign({}, this.orderParams, {
          reason: this.notes.reason,
          address: this.notes.address,
          investigation: {
            code: this.notes.investigationCode,
            subcode: this.notes.investigationSubCode,
          },
          representative: {
            first_name: this.notes.representativeFirstName,
            last_name: this.notes.representativeLastName,
            email: this.notes.representativeEmail,
            phone: this.notes.representativePhone,
          },
        }));
      }
    } else if (!this.isContactPersonRequired()) {
      if (this.activeSearchType === 'italy') {
        this.placeOrderNationalStart(Object.assign({}, this.orderParams, {
          reason: this.notes.reason,
          investigation: {
            code: this.notes.investigationCode,
            subcode: this.notes.investigationSubCode,
          },
        }));
      } else {
        this.placeOrderForeignStart(Object.assign({}, this.orderParams, {
          reason: this.notes.reason,
          investigation: {
            code: this.notes.investigationCode,
            subcode: this.notes.investigationSubCode,
          },
        }));
      }
    }

    this.credits.left = creditsLeft;
  }

  getDifferentLegalHeadOfficeMessage(province) {
    return this.$translate.instant('COMPANY_DIFFERENT_LEGAL_HEAD_OFFICE', { province });
  }

  getLoadingMessageKey() {
    return this.activeStep >= 1 && this.productSelected
      ? 'PLACING_ORDER_LOADING'
      : 'GENERIC_LOADING';
  }

  isContactPersonRequired() {
    return this.orderParams && !(/QUICK_REPORT|BASE|EXISTING/gm).test(this.orderParams.product);
  }
}

OrdersWizard.$inject = ['$scope',
  '$ngRedux',
  'actionsFactory',
  'companiesService',
  '$anchorScroll',
  '$location',
  '$translate',
];

export const ordersWizard = {
  template: ordersWizardTpl,
  controller: OrdersWizard,
};
